import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';

export default function HeroModel() {
  const mountRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 5000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    const mountEl = mountRef.current;
    mountEl.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambient);
    const key = new THREE.DirectionalLight(0xffffff, 1.2);
    key.position.set(40, 60, 80);
    scene.add(key);
    const fill = new THREE.PointLight(0xb3162b, 1.6, 3000);
    fill.position.set(-120, 40, 80);
    scene.add(fill);

    const loader = new STLLoader();
    let mesh;
    let targetRot = new THREE.Vector2(0, 0);
    let currentRot = new THREE.Vector2(0, 0);
    let targetPosZ = 180;
    let currentPosZ = 180;
    let targetPosY = 0;
    let currentPosY = 0;
    let cursorTarget = new THREE.Vector2(0, 0);
    let cursorCurrent = new THREE.Vector2(0, 0);

    loader.load('/models/main-asm.stl', (geometry) => {
      geometry.center();
      geometry.computeBoundingBox();
      const bounds = geometry.boundingBox;
      const size = new THREE.Vector3();
      bounds.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = 160 / maxDim;
      const material = new THREE.MeshStandardMaterial({
        color: new THREE.Color('#2b0c12'),
        metalness: 0.7,
        roughness: 0.2,
        emissive: new THREE.Color('#6b0f1a'),
        emissiveIntensity: 0.9,
      });
      mesh = new THREE.Mesh(geometry, material);
      mesh.scale.setScalar(scale);
      mesh.rotation.x = -Math.PI / 2;
      scene.add(mesh);
      resize();
    });

    const resize = () => {
      const width = mountEl.clientWidth;
      const height = mountEl.clientHeight;
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.position.set(0, 0, currentPosZ);
      camera.position.y = currentPosY;
      camera.updateProjectionMatrix();
    };

    const onResize = () => resize();
    window.addEventListener('resize', onResize);

    const onScroll = () => {
      const progress = Math.min(window.scrollY / window.innerHeight, 2.5);
      targetRot.x = -Math.PI / 2 + progress * 0.25;
      targetRot.y = progress * 0.5;
      targetPosZ = 180 - progress * 30;
      targetPosY = -progress * 6;
      mountEl.style.transform = `translate3d(0, ${window.scrollY * 0.35}px, 0)`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    const onMove = (event) => {
      const nx = (event.clientX / window.innerWidth) * 2 - 1;
      const ny = (event.clientY / window.innerHeight) * 2 - 1;
      cursorTarget.set(nx, ny);
    };
    window.addEventListener('mousemove', onMove);

    let raf;
    const animate = () => {
      if (mesh) {
        currentRot.x += (targetRot.x - currentRot.x) * 0.04;
        currentRot.y += (targetRot.y - currentRot.y) * 0.04;
        mesh.rotation.x = currentRot.x;
        mesh.rotation.y = currentRot.y;
        mesh.rotation.z += 0.001;
        cursorCurrent.x += (cursorTarget.x - cursorCurrent.x) * 0.08;
        cursorCurrent.y += (cursorTarget.y - cursorCurrent.y) * 0.08;
        mesh.position.x = cursorCurrent.x * 12;
        mesh.position.y = cursorCurrent.y * -8;
      }
      currentPosZ += (targetPosZ - currentPosZ) * 0.04;
      currentPosY += (targetPosY - currentPosY) * 0.04;
      camera.position.z = currentPosZ;
      camera.position.y = currentPosY;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    resize();

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
      renderer.dispose();
      mountEl.innerHTML = '';
    };
  }, []);

  return <div className="model-backdrop" ref={mountRef} aria-hidden="true" />;
}
